import React, { type MouseEvent, type PropsWithChildren, useMemo } from 'react';
import {
  type GDSHeadingLevel,
  type GDSStatus,
  type GDSStatusBannerProps,
  type GDSTextActionIndicator,
  type GDSTextActionProps,
  HtmlToReact,
  Icon,
  StackItem,
  StatusBanner,
  TextAction,
  UtilityText,
  Box,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';

import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import {
  MasonryDeeplinkAction,
  MasonryDismissAction,
  MasonryDriverAction,
} from '#types/masonry-driver-node-actions.ts';
import { normalizeInternalUrl } from '@leagueplatform/web-common';
import { MasonryLeadingIcon } from '#common/leading-icon/leading-icon.type.ts';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

type MasonryStatusBannerNodeProperties = {
  heading: string;
  headingLevel?: GDSHeadingLevel;
  status?: GDSStatus;
  body?: string;
  leadingIcon?: MasonryLeadingIcon;
  headerActionText?: string;
  headerActionIndicator?: GDSTextActionIndicator;
  footerActionText?: string;
  footerActionIndicator?: GDSTextActionIndicator;
};

type MasonryStatusBannerNodeActions = {
  onDismiss?: MasonryDismissAction;
  onFooterClick?: MasonryDeeplinkAction;
  onHeaderClick?: MasonryDriverAction;
};

type MasonryStatusBannerNodeSections = {};

export type MasonryStatusBannerNode = MasonryEngineNode<
  'statusBanner',
  MasonryStatusBannerNodeProperties,
  MasonryStatusBannerNodeActions,
  MasonryStatusBannerNodeSections
>;

export type MasonryStatusBannerRendererProps =
  MasonryEngineNodeRendererProps<MasonryStatusBannerNode>;

export const DefaultLeadingIcon: MasonryLeadingIcon = {
  type: 'default',
  value: '',
  iconAltText: '',
};

const deriveStatusBannerIcon = ({
  type,
  value,
  iconAltText,
}: MasonryLeadingIcon = DefaultLeadingIcon) => {
  switch (type) {
    case 'emoji':
    case 'url':
      return (
        <Icon
          data-testid="status-banner-icon"
          icon={value}
          label={iconAltText}
        />
      );
    case 'default':
    default:
      return undefined;
  }
};

const TextActionStackItem = ({
  indicator,
  onClick,
  children,
}: PropsWithChildren<GDSTextActionProps<'a'>>) => (
  <StackItem shrinkFactor={0}>
    <TextAction indicator={indicator} onClick={onClick}>
      {children}
    </TextAction>
  </StackItem>
);

export const MasonryStatusBannerRenderer = ({
  status = 'info',
  heading,
  body,
  footerActionText,
  footerActionIndicator,
  headerActionIndicator,
  headerActionText,
  headingLevel = '3',
  leadingIcon,
  onDismiss,
  onFooterClick,
  onHeaderClick,
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryStatusBannerRendererProps) => {
  const invokeAction = useInvokeAction();

  /** The GDS status banner knows to display the dismiss icon if it receives a valid callback, or `undefined`  */
  const onDismissClick: GDSStatusBannerProps['onDismissClick'] = useMemo(
    () =>
      onDismiss
        ? (event: MouseEvent) => invokeAction({ event, ...onDismiss })
        : undefined,
    [invokeAction, onDismiss],
  );
  return (
    <StatusBanner
      data-testid={locatorId}
      status={status}
      title={heading}
      headingLevel={headingLevel}
      icon={deriveStatusBannerIcon(leadingIcon)}
      iconLabel={leadingIcon?.iconAltText}
      onDismissClick={onDismissClick}
      textAction={
        headerActionText && onHeaderClick ? (
          <TextActionStackItem
            indicator={headerActionIndicator}
            onClick={(event) => invokeAction({ event, ...onHeaderClick })}
          >
            {headerActionText}
          </TextActionStackItem>
        ) : undefined
      }
      css={{ justifyContent: 'flex-start', ...css }}
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
    >
      {body && (
        <Box css={{ marginTop: '$quarter', marginBottom: '$half' }}>
          <HtmlToReact
            htmlString={body}
            options={{
              ...BodyTextHtmlTagOptions,
              p: {
                component: UtilityText,
                props: {
                  size: 'sm',
                  as: 'p',
                  css: {
                    '> a': {
                      textDecoration: 'underline',
                    },
                  },
                },
              },
            }}
          />
        </Box>
      )}

      {onFooterClick && footerActionText && (
        <TextAction
          css={{ alignSelf: 'flex-start', typography: '$buttonTwo' }}
          indicator={footerActionIndicator}
          href={normalizeInternalUrl(onFooterClick?.payload?.url)}
          onClick={(event: MouseEvent) =>
            invokeAction({ event, ...onFooterClick })
          }
        >
          {footerActionText}
        </TextAction>
      )}
    </StatusBanner>
  );
};
